import React from "react";
import { Box, SimpleGrid, Text, useColorMode } from "@chakra-ui/react";
import { Chart } from "react-google-charts";
import InsightsSection from "./InsightsSection";
import PlaceholderTile from "./PlaceholderTile";

const AccountValueSection = ({ accountValueMetrics = {}, currencySymbol = "$" }) => {
  const { colorMode } = useColorMode();
  const cardBg = colorMode === "light" ? "gray.50" : "gray.600";
  const labelColor = colorMode === "light" ? "gray.600" : "gray.300";

  const hasData = accountValueMetrics && Object.keys(accountValueMetrics).length > 0;
  const series = Array.isArray(accountValueMetrics.drawdown_series)
    ? accountValueMetrics.drawdown_series
    : [];

  // Parse the YYYY-MM-DD strings into Date objects so Google Charts can pick
  // sensible tick spacing automatically — passing strings makes it render
  // every label and crowd them past readability.
  const chartData = series.length
    ? [
        [
          { label: "Date", type: "date" },
          "Account Value",
          "High Water",
        ],
        ...series.map((p) => {
          const [y, m, d] = p.date.split("-").map((n) => parseInt(n, 10));
          return [new Date(y, m - 1, d), Number(p.value), Number(p.high_water)];
        }),
      ]
    : null;

  const chartOptions = {
    chartArea: { width: "80%", height: "70%" },
    backgroundColor: colorMode === "light" ? "#FDFDFD" : "#1A202C",
    legend: { position: "top", textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" } },
    colors: ["#3182CE", "#F6AD55"],
    hAxis: {
      format: "MMM d",
      textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      slantedText: true,
      slantedTextAngle: 30,
      gridlines: { count: 6 },
      minorGridlines: { count: 0 },
    },
    vAxis: {
      title: `Account Value (${currencySymbol})`,
      titleTextStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
    },
  };

  return (
    <InsightsSection
      title="Account Value Metrics"
      description="Drawdown, equity high-water mark, and period returns."
    >
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Max Drawdown</Text>
          <Text fontSize="lg" fontWeight="bold" color="red.400">
            {hasData && accountValueMetrics.max_drawdown_pct != null
              ? `${accountValueMetrics.max_drawdown_pct}%`
              : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Current Drawdown</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasData && accountValueMetrics.current_drawdown_pct != null
              ? `${accountValueMetrics.current_drawdown_pct}%`
              : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Equity High</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasData && accountValueMetrics.equity_high_water != null
              ? `${currencySymbol}${Number(accountValueMetrics.equity_high_water).toFixed(0)}`
              : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Period Return</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasData && accountValueMetrics.period_return_pct != null
              ? `${accountValueMetrics.period_return_pct}%`
              : "—"}
          </Text>
        </Box>
      </SimpleGrid>

      {series.length ? (
        <Box bg={colorMode === "light" ? "white" : "gray.700"} rounded="md" h="280px">
          <Chart
            chartType="LineChart"
            width="100%"
            height="100%"
            data={chartData}
            options={chartOptions}
          />
        </Box>
      ) : (
        <PlaceholderTile message="No account value history yet — log account values to see drawdown." />
      )}
    </InsightsSection>
  );
};

export default AccountValueSection;
